<template>
  <!-- <iframe
      :src="`${OIDC_AUTHORITY}/cookietest.html`"
      class="hidden"
      v-if="isLoggedIn"
    /> -->
  <div class="site-loader" v-show="!appLoaded">
    <div></div>
    <div></div>
  </div>

  <component :is="currentComponentName" v-if="currentComponentName" />
  <router-view v-else />
  <q-no-ssr>
    <DisclaimerDialog />
    <AnnouncementDialog />

    <template v-if="businessConfigurations.isStoreAgeRestricted">
      <PersonalInfoDialog />
      <EAIVSpinner />
    </template>

    <div class="iframe-container">
      <LNSIFrame />
    </div>

    <!-- Install PWA Dialog -->
    <InstallationBanner
      v-if="isBeforeInstallPromptSupported"
      :show="appLoaded && showPWAInstallPopup"
      is-dialog
    />
    <InstallationInstructions
      v-else
      :show="appLoaded && showPWAInstallPopup"
      is-dialog
    />
    <!-- Install PWA Dialog -->
  </q-no-ssr>
  <q-resize-observer @resize="onPageResize" />
</template>

<script>
import NoConnection from 'components/NoConnection'
import StoreNotActive from 'components/StoreNotActive'
import BetaModeDialog from 'components/common/modes/BetaModeDialog'
import TestModeDialog from 'components/common/modes/TestModeDialog'
import OfflineModeDialog from 'components/common/modes/OfflineModeDialog'

import { throttle, Platform, createMetaMixin, SessionStorage } from 'quasar'
import LNSIFrame from 'components/LNS/IFrame'
import { reloadPage, isDateEqual } from 'src/utils'
import {
  setAVSessionID,
  removeAVSessionID,
  tempCacheStorage,
} from 'src/boot/axios'
import EAIVSpinner from 'components/dialog/eaiv/EAIVSpinner'
import HistoryMixin from 'src/utils/HistoryMixin'
import IdentityMixin from 'src/utils/IdentityMixin'
import DisclaimerAnnouncementMixin from 'src/utils/DisclaimerAnnouncementMixin'
// import { iosPWASplash } from 'src/utils'
import {
  isSsr,
  appConfig,
  isDev,
  ageRestrictedBrowsingPages,
} from 'src/constants'
import { defineAsyncComponent } from 'vue'
import { useAgeVerificationStore } from 'src/stores/age-verification'
import { useCustomerStore } from './stores/customer'
import { useAuthStore } from 'src/stores/auth'
import { useNotificationStore } from './stores/notification'
import { usePersistedLocalStore } from 'src/stores/persisted-local'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'
import { mapState } from 'pinia'
import {
  handleWindowError,
  isPWA,
  isBeforeInstallPromptSupported,
} from 'src/utils'
import { usePlatform } from 'src/composables/usePlatform'
import { usePageMeta } from 'src/composables/usePageMeta'

var pwaEventObj = null

export default {
  name: 'App',
  data() {
    return {
      customerStore: useCustomerStore(),
      notificationStore: useNotificationStore(),
      ageVerificationStore: useAgeVerificationStore(),
      authStore: useAuthStore(),
      persistedLocalStore: usePersistedLocalStore(),
      persistedStore: usePersistedStore(),
      commonStore: useCommonStore(),
      // OIDC_AUTHORITY: appConfig.OIDC_AUTHORITY,
      ageRestrictedBrowsingPages: ageRestrictedBrowsingPages,
    }
  },
  mixins: [
    createMetaMixin(function () {
      const { titleTemplate } = usePageMeta()
      return {
        title: 'Online Shopping',
        titleTemplate,
        ...this.newCommonMeta,
      }
    }),
    HistoryMixin,
    IdentityMixin,
    DisclaimerAnnouncementMixin,
  ],
  components: {
    NoConnection,
    BetaModeDialog,
    StoreNotActive,
    TestModeDialog,
    OfflineModeDialog,
    EAIVSpinner,
    LNSIFrame,
    PersonalInfoDialog: defineAsyncComponent(
      () => import('components/dialog/eaiv/PersonalInfoDialog.vue')
    ),
    AgeVerification: defineAsyncComponent(
      () => import('src/pages/AgeVerification.vue')
    ),
    DisclaimerDialog: defineAsyncComponent(
      () => import('components/dialog/DisclaimerDialog.vue')
    ),
    AnnouncementDialog: defineAsyncComponent(
      () => import('components/dialog/AnnouncementDialog.vue')
    ),
    InstallationBanner: defineAsyncComponent(
      () => import('components/pwa/InstallationBanner.vue')
    ),
    InstallationInstructions: defineAsyncComponent(
      () => import('components/pwa/InstallationInstructions.vue')
    ),
  },
  computed: {
    ...mapState(useCommonStore, [
      'appLoaded',
      'networkStatus',
      'storeConfigurationLoaded',
      'tenantFound',
      'storeIsActive',
      'showPWAInstallPopup',
    ]),
    ...mapState(usePersistedStore, [
      'activeMode',
      'eaivPersistedSessionId',
      'isBetaModeActive',
      'isTestModeActive',
      'isOfflineModeActive',
      'isBetaAccessCodeValidated',
    ]),
    ...mapState(usePersistedLocalStore, ['isPWAPopupDenied']),
    eaivSessionId() {
      return this.$route.query?.sessionId
    },
    currentComponentName() {
      let component

      if (!this.networkStatus) component = 'NoConnection'
      else if (this.isBetaModeActive && !this.isBetaAccessCodeValidated)
        component = 'BetaModeDialog'
      else if (this.isTestModeActive && !this.isBetaAccessCodeValidated)
        component = 'TestModeDialog'
      else if (this.isOfflineModeActive && !this.isBetaAccessCodeValidated)
        component = 'OfflineModeDialog'
      else if (
        (this.storeConfigurationLoaded &&
          !(this.tenantFound && this.storeIsActive)) ||
        this.activeMode == 6
      )
        component = 'StoreNotActive'
      else if (
        this.isCordova &&
        this.isAgeVerificationRequired &&
        this.$route.name &&
        !this.$route.meta?.skipAgeVerification
      )
        component = 'AgeVerification'

      return component
    },
    allowedBrowsers() {
      let isShow = false
      if (
        Platform?.is?.mobile &&
        (Platform?.is?.android || (Platform?.is?.ios && Platform?.is?.safari))
      )
        isShow = true
      return isShow
    },
    showIdentityWidget() {
      return !!(
        this.isAgeVerificationRequired &&
        this.$route.name &&
        !this.$route.meta?.skipAgeVerification &&
        !this.currentComponentName
      )
    },
    isPWA,
    isBeforeInstallPromptSupported,
  },
  created() {
    const _this = this
    if (this.isCordova) {
      document.addEventListener(
        'deviceready',
        () => {
          this.commonStore.setState({
            key: 'NetworkConnectionStatus',
            value: this.checkConnection(),
          })

          document.addEventListener(
            'offline',
            () => {
              this.commonStore.setState({
                key: 'NetworkConnectionStatus',
                value: 0,
              })
            },
            false
          )

          document.addEventListener(
            'online',
            () => {
              if (this.isCordova) {
                this.$router.go(0).finlly(() =>
                  this.commonStore.setState({
                    key: 'NetworkConnectionStatus',
                    value: 1,
                  })
                )
              } else {
                this.commonStore.setState({
                  key: 'NetworkConnectionStatus',
                  value: 1,
                })
              }
            },
            false
          )

          document.addEventListener(
            'touchend',
            throttle(() => {
              if (_this.isLoggedIn) {
                _this.authStore.getUserInfo()
                _this.notificationStore.getAllNotification()
              }
            }, 5000),
            false
          )
        },
        false
      )

      if (typeof window !== 'undefined') {
        window.handleOpenURL = (url) => {
          let splitUrl = url.split('://')
          if (!splitUrl[1]) reloadPage()
          if (splitUrl[1] && /sessionId=/.test(splitUrl[1])) {
            let callbackParams = splitUrl[1].split('?')
            let params = new URLSearchParams(callbackParams[1])
            if (params.get('sessionId')) {
              this.ageVerificationStore.setLoading(true)
              this.fetchSessionDetail(params.get('sessionId'))
            }
          } else {
            this.changeDialogState({
              dialog: 'accountDrawer',
              val: false,
            })

            let splitUrl = url.split('://')

            if (!splitUrl[1]) reloadPage()
            this.$router.push(splitUrl[1])
          }
        }
      }
    }
  },
  beforeMount() {
    if (isSsr && this.$route.name !== 'Callback')
      this.commonStore.getConfiguration()
  },
  mounted() {
    const _this = this
    _this.loadExternalFiles()
    window.onfocus = () => {
      let promises = []
      if (this.isLoggedIn) {
        promises = [
          this.authStore.getUserInfo(),
          this.notificationStore.getAllNotification(),
        ]
      }
      if (this.activeMode == 1)
        promises.push(this.commonStore.getConfiguration())

      Promise.all(promises).finally(() => {})
    }

    handleWindowError()

    // window.addEventListener(
    //   'message',
    //   (evt) => {
    //     if (evt.data === 'FTX:3PCsupported') {
    //       this.$oidc.events.addUserSignedOut(async () => {
    //         //this.$oidc.signoutRedirect() //the reason I removed this is, 3PC is called when session is endned by another app/tab, in that case when we make end session request again it fails as session was ended by another app already.
    //         // reloadPage()

    //         let userInfo = await _this.$store.dispatch('auth/oidcGetUser')
    //         if (userInfo && userInfo.access_token)
    //           _this.$store.dispatch('auth/logout')
    //         else reloadPage()
    //       })
    //     }
    //     // else if (evt.data === 'MM:3PCunsupported') console.log('Not Supported')
    //   },
    //   false
    // )
    document.addEventListener('deviceready', this.onDeviceReady, false)

    document.getElementById('mainPreLoader')?.remove()

    /* Start: PWA installation popup */
    if (_this.isBeforeInstallPromptSupported) {
      _this.emitter.on('handle-pwa-action', _this.handlePWAAction)

      window.addEventListener('beforeinstallprompt', (event) => {
        if (!_this.isMobile) return
        event.preventDefault()
        pwaEventObj = event

        _this.commonStore.updateState({
          key: 'showPWAInstallPopupSidebar',
          val: true,
        })

        !_this.isPWAPopupDenied &&
          _this.commonStore.updateState({
            key: 'showPWAInstallPopup',
            val: true,
          })
      })
    } else if (
      !!_this.isMobile &&
      !_this.isPWA &&
      !_this.isPWAPopupDenied &&
      _this.allowedBrowsers
    ) {
      _this.commonStore.updateState({
        key: 'showPWAInstallPopup',
        val: true,
      })
    }

    if (!this.isCordova) {
      window.addEventListener(
        'offline',
        () => {
          let networkState = navigator.onLine ? 1 : 0
          this.handleNetworkState(networkState)
        },
        false
      )

      window.addEventListener(
        'online',
        () => {
          let networkState = navigator.onLine ? 1 : 0
          this.handleNetworkState(networkState)
        },
        false
      )
    }

    // if (!this.isCordova && !this.instanceLoaded) this.loadIdentityInstance()
    if (!this.isCordova && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered Commerce:', registration)
        })
        .catch((error) => {
          console.error('Service Worker registration failed Commerce:', error)
        })
    }
  },
  watch: {
    eaivSessionId(newVal, oldVal) {
      if (newVal && newVal != oldVal && !this.isCordova) {
        this.ageVerificationStore.setLoading(true)
        this.fetchSessionDetail(this.eaivSessionId)
      }
    },
    storeConfigurationLoaded(newVal, oldVal) {
      if (newVal && newVal != oldVal) this.loadExternalFiles()
    },
    showIdentityWidget(newVal, oldVal) {
      if (
        !this.isCordova &&
        this.instanceLoaded &&
        !this.identityInstanceInitialized &&
        newVal &&
        newVal != oldVal
      )
        this.handleIdentityCallback()
    },
    // businessConfigurations: {
    //   handler(newVal, oldVal) {
    //     if (newVal != oldVal) {
    //       this.setIosPWASplash()
    //     }
    //   },
    //   deep: true,
    // },
  },
  methods: {
    onPageResize(pageSize) {
      this.commonStore.$patch({ windowDimensions: pageSize })
    },
    onDeviceReady() {
      window.open = cordova.InAppBrowser.open
    },
    checkConnection() {
      let networkState = navigator.connection.type

      return networkState == Connection.UNKNOWN ||
        networkState == Connection.NONE
        ? 0
        : 1
    },
    verificationComplete(sessionId, routeName, isSuccess = true) {
      tempCacheStorage.clear()
      this.ageVerificationStore
        .verificationComplete({
          sessionId,
          params: { isInfoMismatched: false },
        })
        .then((response) => {
          if (isSuccess) {
            if (this.isLoggedIn) {
              this.authStore.getUserInfo()
              this.customerStore.setCustomerDetails({
                ...this.customer,
                isVerifiedOnline: true,
              })
            }
            if (
              this.showEAIVPage ||
              this.ageRestrictedPages.includes(routeName) === false
            )
              this.onEaivSuccess({
                eaivSessionId: sessionId,
                routeName: routeName,
              })
            else
              this.emitter.emit('eaivVerificationSuccess', {
                eaivSessionId: sessionId,
                routeName: routeName,
              })
            this.persistedStore.setField({
              field: 'eaivPersistedVerificationSuccess',
              value: true,
            })

            this.changeDialogState({
              dialog: 'MismatchPersonalInfo',
              val: false,
              properties: {},
            })

            if (
              !this.isLoggedIn &&
              routeName != 'Checkout' &&
              this.businessConfigurations?.isStoreAgeRestricted &&
              this.businessConfigurations?.enableAgeRestrictedBrowsing
            )
              this.changeDialogState({
                dialog: 'AskForSignup',
                val: true,
                properties: {},
              })
          } else {
            if (
              this.showEAIVPage ||
              this.ageRestrictedPages.includes(routeName) === false
            )
              this.onEaivFail({
                eaivSessionId: sessionId,
                routeName: routeName,
              })
            else
              this.emitter.emit('eaivVerificationFailed', {
                eaivSessionId: sessionId,
                routeName: routeName,
              })
          }
        })

      // Remove skip from persisted
      this.ageVerificationStore.setField({
        field: 'skipBrowsingAgeRestrictedProd',
        value: false,
      })
    },
    onCancelVerification(eaivSessionId) {
      tempCacheStorage.clear()
      this.ageVerificationStore
        .verificationComplete({
          sessionId: eaivSessionId,
          params: { isInfoMismatched: true },
        })
        .then((response) => {
          this.persistedStore.setField({
            field: 'eaivPersistedSessionId',
            value: null,
          })
          this.ageVerificationStore.resetVerificationStatus()
          this.changeDialogState({
            dialog: 'MismatchPersonalInfo',
            val: false,
            properties: {},
          })
        })
    },
    fetchSessionDetail(sessionId = null) {
      if (!sessionId) return
      tempCacheStorage.clear()
      let routeName = this.$route.name,
        mismatchedFieldName = []
      this.ageVerificationStore
        .checkStatus(sessionId)
        .then((response) => {
          this.ageVerificationStore.setLoading(false)
          if (
            response.success &&
            response.data?.isVerified === true &&
            this.eaivPersistedSessionId === sessionId
          ) {
            let personalInfo = response.data?.personalInfo,
              preFillInformation = response.data?.preFillInformation

            if (
              (this.isLoggedIn ||
                (!this.isLoggedIn && routeName == 'Checkout')) &&
              personalInfo &&
              preFillInformation &&
              (personalInfo?.firstName != preFillInformation?.firstName ||
                personalInfo?.lastName != preFillInformation?.lastName ||
                isDateEqual(personalInfo?.dob, preFillInformation?.dob) ==
                  false)
            ) {
              if (personalInfo?.firstName != preFillInformation?.firstName)
                mismatchedFieldName.push('firstName')
              if (personalInfo?.lastName != preFillInformation?.lastName)
                mismatchedFieldName.push('lastName')
              if (
                isDateEqual(personalInfo?.dob, preFillInformation?.dob) == false
              )
                mismatchedFieldName.push('dob')

              this.changeDialogState({
                dialog: 'MismatchPersonalInfo',
                val: true,
                properties: {
                  personalInfo,
                  preFillInformation,
                  mismatchedFieldName,
                  onContinue: this.verificationComplete,
                  onCancel: this.onCancelVerification,
                },
              })
            } else {
              this.verificationComplete(sessionId, routeName, true)
            }
          } else {
            this.verificationComplete(sessionId, routeName, false)
          }
        })
        .catch(() => {
          this.ageVerificationStore.setLoading(false)
        })
    },
    removeQueryParams(eaivSessionId, isSuccess) {
      if (isSuccess) setAVSessionID(eaivSessionId)
      else {
        this.persistedStore.setField({
          field: 'eaivPersistedSessionId',
          value: null,
        })
        removeAVSessionID()
      }

      this.ageVerificationStore.setVerificationStatus(isSuccess)
      // remove sessionId from URL
      let query = Object.assign({}, this.$route.query)
      let params = Object.assign({}, this.$route.params)
      delete query.sessionId
      this.$router.replace({ query, params }).catch(() => {})
    },
    async onEaivSuccess({ eaivSessionId, routeName }) {
      if (!eaivSessionId || !routeName) return
      this.removeQueryParams(eaivSessionId, true)

      if (!this.showEAIVPage) {
        tempCacheStorage.clear()
        setTimeout(() => {
          this.ageVerificationStore.setLoading(false)
          this.showSuccess('Age Verified Successfully.')
        }, 500)
      } else {
        setTimeout(() => {
          this.ageVerificationStore.setLoading(false)
        }, 500)
      }
    },
    async onEaivFail({ eaivSessionId, routeName }) {
      if (!eaivSessionId || !routeName) return
      this.removeQueryParams(eaivSessionId, false)
      if (!this.showEAIVPage) {
        tempCacheStorage.clear()
        setTimeout(() => {
          this.ageVerificationStore.setLoading(false)
          this.changeDialogState({
            dialog: 'VerificationFailDialog',
            val: true,
            properties: {},
          })
        }, 500)
      } else {
        setTimeout(() => {
          this.ageVerificationStore.setLoading(false)
        }, 500)
      }
    },
    handlePWAAction(isAccepted) {
      const _this = this
      if (!pwaEventObj) return
      if (isAccepted && pwaEventObj)
        pwaEventObj.prompt()?.then((pRes) => {
          if (pRes?.accepted === 'accepted') {
            _this.commonStore.updateState({
              key: 'showPWAInstallPopupSidebar',
              val: false,
            })
          }
        })
      this.commonStore.updateState({
        key: 'showPWAInstallPopup',
        val: false,
      })

      if (!isAccepted)
        this.persistedLocalStore.setField({
          field: 'isPWAPopupDenied',
          value: true,
        })
    },
    handleNetworkState(networkState) {
      let element = document.getElementsByTagName('html')
      if (element?.length) {
        element[0].style.cssText = !networkState
          ? 'filter: grayscale(1); pointer-events: none;'
          : ''
      }
    },
    // setIosPWASplash() {
    //   this.$nextTick(() => {
    //     let checkMetaExists = document.querySelector(
    //       "meta[property='application-name']"
    //     )

    //     if (checkMetaExists) return
    //     else if (
    //       Platform?.is?.mobile &&
    //       Platform?.is?.ios &&
    //       this.businessConfigurations.pwaImageUrl
    //     )
    //       iosPWASplash(this.businessConfigurations.pwaImageUrl, {
    //         appTitle: this.businessConfigurations.businessName,
    //         splashColor: 'white',
    //       })
    //   })
    // },
    loadExternalFiles() {
      const _this = this
      if (!this.storeConfigurationLoaded) return
      /* START: Load external CSS & JS */
      try {
        function loadScript(src, callback) {
          if (document.querySelector(`script[src="${src}"]`)) {
            console.warn('Script already loaded!')
            return
          }

          const script = document.createElement('script')
          script.src = src
          script.async = true
          script.onload = callback
          if (/cdn.userway.org/.test(src)) script.dataset.account = 'Eeww75jg0O'
          document.body.appendChild(script)
        }

        function loadCSS(href, callback) {
          // Check if the CSS is already loaded
          if (document.querySelector(`link[href="${href}"]`)) {
            console.warn(`CSS already loaded`)
            return
          }

          // Create a new <link> element
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          link.type = 'text/css'
          link.onload = callback
          document.head.appendChild(link)
        }

        function loadFont(fontUrl, callback) {
          // Check if the font is already loaded
          if (document.querySelector(`link[href="${fontUrl}"]`)) {
            console.warn(`Font already loaded`)
            return
          }

          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = fontUrl
          link.onload = callback
          document.head.appendChild(link)
        }

        /* START: Age Gate Widget integration */
        this.$nextTick(() => {
          if (!_this.isCordova) {
            if (!_this.instanceLoaded)
              _this.loadIdentityInstance(_this.handleIdentityCallback)
            else _this.handleIdentityCallback()
          }
        })
        /* END: Age Gate Widget integration */

        const datacapScriptURI =
          appConfig.DATA_CAP_ON_IFRAME == 'true'
            ? `${appConfig.DATA_CAP_TOKEN_URL}/v1/client/hosted`
            : `${appConfig.DATA_CAP_TOKEN_URL}/v1/client`
        loadScript(datacapScriptURI, () => {})

        if (this.isLoggedIn) {
          loadScript(
            'https://www.gstatic.com/firebasejs/8.2.3/firebase-app.js',
            () => {}
          )
          loadScript(
            'https://www.gstatic.com/firebasejs/8.2.3/firebase-messaging.js',
            () => {}
          )
        }

        if (!_this.isCordova) {
          // Load CSS
          loadCSS(`${appConfig.EAIV_ExternalURL}/build/index.css`, () => {})

          /* loadScript(`${appConfig.EAIV_ExternalURL}/build/index.js`, () => {
            if (!_this.instanceLoaded) _this.loadIdentityInstance()
          }) */
        }
        if (
          appConfig.ENABLE_GOOGLE_AUTOCOMPLETE &&
          appConfig.GOOGLE_MAP_API_KEY
        ) {
          loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${appConfig.GOOGLE_MAP_API_KEY}&libraries=places,marker&loading=async`,
            () => {}
          )
        }

        loadFont(
          'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;700&family=Lato:wght@400;700&family=Poppins:wght@400;500;600;700&display=swap',
          () => {}
        )
        /* loadFont(
          'https://fonts.googleapis.com/icon?family=Material+Icons',
          () => {}
        ) */

        if (!isDev && !_this.isCordova)
          loadScript('https://cdn.userway.org/widget.js', () => {})
        /* END: Load external CSS & JS */
      } catch (error) {
        console.log(error)
      }
    },
    getIdentityInstanceConfig() {
      const _this = this
      let config = {
        apiKey: this.businessConfigurations?.identityKey || '',
        btnVerifyColor: this.getCssVariable('--q-secondary'),
        pageVerificationType: 'basic',
        verifyDob: false,
        showSignIn: false,
        allowSkip: true,
        showClose: true,
        skipCallback: function () {
          _this.eaivWidgetActionCallback(true)
        },
      }

      if (this.businessConfigurations?.enableAgeRestrictedBrowsing) {
        config = {
          ...config,
          pageVerificationType: 'advanced',
          onSuccess: this.identitySuccessHandler,
          onError: this.identityErrorHandler,
          styleOptions: {
            height: `calc(100dvh - ${this.getHeaderHeight}px)`,
            top: `${this.getHeaderHeight}px`,
          },
          keepSession: true,
        }

        if (!this.isLoggedIn) {
          config = {
            ...config,
            showSignIn: true,
            signInButtonText: 'Sign In',
            signinCallback: function () {
              SessionStorage.setItem('awaitingThirdPartyReturn', true)
              _this.actionLogin(false)
            },
          }
        }
      } else {
        config = {
          ...config,
          passVerificationCallback: function () {
            _this.eaivWidgetActionCallback(false)
          },
        }
      }
      return config
    },
    handleIdentityCallback() {
      this.setHeaderHeight()
      if (
        this.showIdentityWidget &&
        !this.ageVerificationStore.isUserAgeVerified
      ) {
        let config = this.getIdentityInstanceConfig()
        this.initEAIVPopup({ ...config, verificationDuring: 0 })
      }

      /* Reset verification on redirect back from core */
      const isReturnFromCore = !!SessionStorage.getItem(
        'awaitingThirdPartyReturn'
      )
      if (isReturnFromCore) {
        if (this.isLoggedIn) {
          SessionStorage.removeItem('awaitingThirdPartyReturn')
        } else if (
          !this.isLoggedIn &&
          this.instanceLoaded &&
          this.identityInstanceInitialized
        ) {
          SessionStorage.removeItem('awaitingThirdPartyReturn')
          this.resetVerification()
        }
      }
      /* Reset verification on redirect back from core */
    },
    eaivWidgetActionCallback(skipBrowsingAgeRestrictedProd = null) {
      if (skipBrowsingAgeRestrictedProd !== null) {
        this.ageVerificationStore.setField({
          field: 'skipBrowsingAgeRestrictedProd',
          value: !!skipBrowsingAgeRestrictedProd,
        })
      }

      this.persistedStore.setField({
        field: 'showEAIVPage',
        value: false,
      })
    },
    getCssVariable(varName) {
      let hexVal = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim()
      return hexVal || ''
    },
    actionLogin(isSignup) {
      this.authStore
        .signIn({
          isSignup,
        })
        .then((user) => {
          if (this.isCordova && user) {
            this.$router.push({ name: 'Callback' })
          }
        })
        .catch((error) => console.log(error))
    },
  },
  setup() {
    const { isMobile, isCordova } = usePlatform()
    return { isMobile, isCordova }
  },
}
</script>
